// frontend/src/components/production-sequence/PriorityConfigPanel.tsx
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LineConfigEditor } from './LineConfigEditor';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, arrayMove, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { filterLabelCls } from '@/components/common/FilterBar';
import type { PriorityConfig, PriorityCriterio, LineConfig, LineId } from '@/types/productionSequence';

function CriterioItem({ c, dark, onToggle, onDir }: {
  c: PriorityCriterio; dark: boolean; onToggle: () => void; onDir: () => void;
}) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: c.field });
  const style: React.CSSProperties = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const box = dark ? 'bg-slate-800 border-white/10' : 'bg-white border-slate-200';
  return (
    <div ref={setNodeRef} style={style} className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border text-[12px] ${box}`}>
      <button {...attributes} {...listeners} className={`cursor-grab active:cursor-grabbing ${dark ? 'text-slate-600' : 'text-slate-300'}`} aria-label="drag">
        <svg className="w-3 h-3" viewBox="0 0 12 12" fill="currentColor"><circle cx="3" cy="2" r="1"/><circle cx="9" cy="2" r="1"/><circle cx="3" cy="6" r="1"/><circle cx="9" cy="6" r="1"/><circle cx="3" cy="10" r="1"/><circle cx="9" cy="10" r="1"/></svg>
      </button>
      <span className={`flex-1 ${dark ? 'text-slate-200' : 'text-slate-700'}`}>{t(c.label)}</span>
      <button onClick={onDir} className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${dark ? 'bg-white/8 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
        {c.direction.toUpperCase()}
      </button>
      <input type="checkbox" checked={c.active} onChange={onToggle} className="cursor-pointer" />
    </div>
  );
}

interface Props {
  open: boolean;
  dark: boolean;
  config: PriorityConfig;
  lines: LineId[];
  lineConfigs: Record<LineId, LineConfig>;
  selectedLine: LineId;
  onClose: () => void;
  onApply: (config: PriorityConfig) => void;
  onReset: () => void;
  onLineConfigChange: (lineId: LineId, partial: Partial<LineConfig>) => void;
}

export function PriorityConfigPanel({ open, dark, config, lines, lineConfigs, selectedLine, onClose, onApply, onReset, onLineConfigChange }: Props) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<PriorityCriterio[]>(config.criterios);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  useEffect(() => { setDraft(config.criterios); }, [config.criterios, open]);

  if (!open) return null;

  function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setDraft(prev => {
      const oldIdx = prev.findIndex(c => c.field === active.id);
      const newIdx = prev.findIndex(c => c.field === over.id);
      return arrayMove(prev, oldIdx, newIdx).map((c, i) => ({ ...c, order: i }));
    });
  }
  function toggle(field: string) { setDraft(prev => prev.map(c => c.field === field ? { ...c, active: !c.active } : c)); }
  function flipDir(field: string) { setDraft(prev => prev.map(c => c.field === field ? { ...c, direction: c.direction === 'asc' ? 'desc' : 'asc' } : c)); }

  const panel = dark ? 'bg-slate-900 border-white/10 text-slate-200' : 'bg-white border-slate-200 text-slate-700';
  const btn = 'text-[12px] font-semibold px-3 py-1.5 rounded-lg cursor-pointer';

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} aria-hidden="true" />
      <aside className={`fixed inset-y-0 right-0 z-50 w-[440px] max-w-full border-l shadow-2xl flex flex-col ${panel}`}>
        <div className={`flex items-center justify-between px-4 py-3 border-b ${dark ? 'border-white/10' : 'border-slate-200'}`}>
          <h2 className="text-[14px] font-semibold">{t('sequence.priority.title')}</h2>
          <button onClick={onClose} className={dark ? 'text-slate-400 hover:text-white' : 'text-slate-400 hover:text-slate-700'} aria-label="close">
            <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 3l10 10M13 3L3 13"/></svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4">
          <div>
            <div className={filterLabelCls(dark)}>{t('sequence.priority.criteriaTitle')}</div>
            <div className={`text-[11px] mb-2 ${dark ? 'text-slate-500' : 'text-slate-400'}`}>{t('sequence.priority.criteriaHint')}</div>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={draft.map(c => c.field)} strategy={verticalListSortingStrategy}>
                <div className="flex flex-col gap-1.5">
                  {draft.map(c => (
                    <CriterioItem key={c.field} c={c} dark={dark} onToggle={() => toggle(c.field)} onDir={() => flipDir(c.field)} />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>

          <div className={`border-t pt-4 ${dark ? 'border-white/10' : 'border-slate-200'}`}>
            <div className={`mb-1 ${filterLabelCls(dark)}`}>{t('sequence.priority.lineConfigTitle')}</div>
            <p className={`text-[11px] mb-2.5 ${dark ? 'text-slate-500' : 'text-slate-400'}`}>{t('sequence.priority.lineConfigSubtitle')}</p>
            <LineConfigEditor
              lines={lines}
              lineConfigs={lineConfigs}
              selectedLine={selectedLine}
              dark={dark}
              onChange={onLineConfigChange}
            />
            <p className={`mt-2.5 text-[11px] ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
              ⓘ {t('sequence.priority.configHint')}
            </p>
          </div>
        </div>

        <div className={`flex items-center justify-between gap-2 px-4 py-3 border-t ${dark ? 'border-white/10' : 'border-slate-200'}`}>
          <button onClick={() => { onReset(); onClose(); }} className={`${btn} ${dark ? 'bg-white/8 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
            {t('sequence.priority.reset')}
          </button>
          <button onClick={() => { onApply({ criterios: draft }); onClose(); }} className={`${btn} bg-sky-500 text-white hover:bg-sky-600`}>
            {t('sequence.priority.apply')}
          </button>
        </div>
      </aside>
    </>
  );
}
